<script setup lang="ts">
import { Select } from 'primevue'
import { useI18n } from 'vue-i18n'
import { usePrimeVue } from 'primevue/config'
import { ref, watch, onMounted } from 'vue'
import { useSessionStore } from '@/stores/session'
import { primevueLocales } from '@/primevue/locales'

const languageNames = {
  en: 'English',
  es: 'Español',
  fr: 'Française',
  msa: 'العربية',
  so: 'Soomaali',
  hmn: 'Hmoob'
}

const sessionStore = useSessionStore()
const i18n = useI18n()
const primevue = usePrimeVue()

const languageOptions = Object.keys(languageNames).map(langCode => ({
  code: langCode,
  name: languageNames[langCode as keyof typeof languageNames]
}))

const selectedLanguage = ref({
  code: sessionStore.getLocaleCode(),
  name: languageNames[sessionStore.getLocaleCode() as keyof typeof languageNames]
})

const updatePrimeVueLocale = (localeCode: string) => {
  if (primevue?.config?.locale) {
    const localeData = primevueLocales[localeCode] || primevueLocales.en
    Object.assign(primevue.config.locale, localeData)
  }
}

const changeLanguageCode = () => {
  const localeCode = selectedLanguage.value.code
  sessionStore.setLocaleCode(localeCode)
  i18n.locale.value = localeCode
  updatePrimeVueLocale(localeCode)
}

const syncLocaleFromSession = (localeCode: string) => {
  i18n.locale.value = localeCode
  updatePrimeVueLocale(localeCode)
  document.documentElement.lang = localeCode
  selectedLanguage.value = {
    code: localeCode,
    name: languageNames[localeCode as keyof typeof languageNames]
  }
}

onMounted(() => {
  syncLocaleFromSession(sessionStore.getLocaleCode())
})

watch(() => sessionStore.getLocaleCode(), syncLocaleFromSession)

watch(selectedLanguage, newLang => {
  document.documentElement.lang = newLang.code
})
</script>

<template>
  <Select
    :options="languageOptions"
    v-model="selectedLanguage"
    optionLabel="name"
    filter
    @change="changeLanguageCode"
  ></Select>
</template>

<style scoped></style>
